// File: src/pages/Procurement/POReceivingDashboard.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/axios";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Boxes,
  Search,
  Download,
  Printer,
  FileSpreadsheet,
  Calendar,
  X,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  PackageCheck,
  AlertTriangle,
  Clock3,
  CheckCircle2,
  Eye,
  Truck,
} from "lucide-react";

import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import { format } from "date-fns";
import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/* ───────────────── helpers ───────────────── */

const asArray = (v) =>
  Array.isArray(v) ? v : [];

const toLower = (v) =>
  String(v || "").toLowerCase();

const ARROW = "\u2192";

function classNames(...xs) {
  return xs
    .filter(Boolean)
    .join(" ");
}

function pad2(v) {
  return String(v).padStart(
    2,
    "0"
  );
}

function formatLocalDateKey(date) {
  if (
    !(date instanceof Date) ||
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  return `${date.getFullYear()}-${pad2(
    date.getMonth() + 1
  )}-${pad2(
    date.getDate()
  )}`;
}

function extractDateOnly(value) {
  if (!value) {
    return "";
  }

  if (
    value instanceof Date &&
    !Number.isNaN(
      value.getTime()
    )
  ) {
    return formatLocalDateKey(
      value
    );
  }

  const raw =
    String(value).trim();

  if (!raw) {
    return "";
  }

  const directDateMatch =
    raw.match(
      /^(\d{4})-(\d{2})-(\d{2})$/
    );

  if (directDateMatch) {
    return directDateMatch[0];
  }

  const leadingDateMatch =
    raw.match(
      /^(\d{4})-(\d{2})-(\d{2})T/
    );

  if (leadingDateMatch) {
    return `${leadingDateMatch[1]}-${leadingDateMatch[2]}-${leadingDateMatch[3]}`;
  }

  const parsed =
    new Date(raw);

  if (
    !Number.isNaN(
      parsed.getTime()
    )
  ) {
    return formatLocalDateKey(
      parsed
    );
  }

  return raw.slice(0, 10);
}

function parseLocalDate(
  yyyyMmDd
) {
  if (!yyyyMmDd) {
    return null;
  }

  const [
    y,
    m,
    d,
  ] =
    String(yyyyMmDd)
      .split("-")
      .map(Number);

  if (
    !y ||
    !m ||
    !d
  ) {
    return null;
  }

  return new Date(
    y,
    m - 1,
    d
  );
}

function startOfDay(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
}

function addDays(
  date,
  delta
) {
  const d =
    startOfDay(date);

  d.setDate(
    d.getDate() +
      delta
  );

  return d;
}

function startOfWeek(date) {
  const d =
    startOfDay(date);

  const weekday =
    (d.getDay() + 6) %
    7;

  return addDays(
    d,
    -weekday
  );
}

function endOfWeek(date) {
  return addDays(
    startOfWeek(date),
    6
  );
}

function startOfMonth(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    1
  );
}

function endOfMonth(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0
  );
}

function addMonths(
  date,
  delta
) {
  return new Date(
    date.getFullYear(),
    date.getMonth() +
      delta,
    1
  );
}

function isSameMonth(
  a,
  b
) {
  return (
    a.getFullYear() ===
      b.getFullYear() &&
    a.getMonth() ===
      b.getMonth()
  );
}

function isSameDay(
  a,
  b
) {
  return (
    a.getFullYear() ===
      b.getFullYear() &&
    a.getMonth() ===
      b.getMonth() &&
    a.getDate() ===
      b.getDate()
  );
}

function formatMonthTitle(
  date
) {
  return new Intl.DateTimeFormat(
    "en-GB",
    {
      month: "short",
      year: "numeric",
    }
  ).format(date);
}

function formatDayTitle(
  date
) {
  return new Intl.DateTimeFormat(
    "en-GB",
    {
      weekday: "long",
      day: "numeric",
      month: "short",
      year: "numeric",
    }
  ).format(date);
}

function formatWeekTitle(
  date
) {
  const start =
    startOfWeek(date);

  const end =
    endOfWeek(date);

  const sameMonth =
    start.getMonth() ===
      end.getMonth() &&
    start.getFullYear() ===
      end.getFullYear();

  const sameYear =
    start.getFullYear() ===
    end.getFullYear();

  if (sameMonth) {
    return `${start.getDate()}–${end.getDate()} ${new Intl.DateTimeFormat(
      "en-GB",
      {
        month: "short",
        year: "numeric",
      }
    ).format(start)}`;
  }

  if (sameYear) {
    return `${new Intl.DateTimeFormat(
      "en-GB",
      {
        day: "numeric",
        month: "short",
      }
    ).format(
      start
    )} – ${new Intl.DateTimeFormat(
      "en-GB",
      {
        day: "numeric",
        month: "short",
        year: "numeric",
      }
    ).format(end)}`;
  }

  return `${new Intl.DateTimeFormat(
    "en-GB",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
    }
  ).format(
    start
  )} – ${new Intl.DateTimeFormat(
    "en-GB",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
    }
  ).format(end)}`;
}

function buildMonthGrid(
  anchorDate
) {
  const first =
    startOfMonth(
      anchorDate
    );

  const firstWeekday =
    (first.getDay() +
      6) %
    7;

  const gridStart =
    addDays(
      first,
      -firstWeekday
    );

  return Array.from(
    {
      length: 42,
    },
    (_, i) =>
      addDays(
        gridStart,
        i
      )
  );
}

function buildWeekGrid(
  anchorDate
) {
  const first =
    startOfWeek(
      anchorDate
    );

  return Array.from(
    {
      length: 7,
    },
    (_, i) =>
      addDays(
        first,
        i
      )
  );
}

function getPeriodRange(
  anchorDate,
  mode
) {
  if (
    mode === "day"
  ) {
    const start =
      startOfDay(
        anchorDate
      );

    return {
      start,
      end: start,
    };
  }

  if (
    mode === "week"
  ) {
    return {
      start:
        startOfWeek(
          anchorDate
        ),
      end:
        endOfWeek(
          anchorDate
        ),
    };
  }

  return {
    start:
      startOfMonth(
        anchorDate
      ),
    end:
      endOfMonth(
        anchorDate
      ),
  };
}

function formatRangeTitle(
  anchorDate,
  mode
) {
  if (
    mode === "day"
  ) {
    return formatDayTitle(
      anchorDate
    );
  }

  if (
    mode === "week"
  ) {
    return formatWeekTitle(
      anchorDate
    );
  }

  return formatMonthTitle(
    anchorDate
  );
}

function shiftViewDate(
  date,
  mode,
  delta
) {
  if (
    mode === "day"
  ) {
    return addDays(
      date,
      delta
    );
  }

  if (
    mode === "week"
  ) {
    return addDays(
      date,
      delta * 7
    );
  }

  return addMonths(
    date,
    delta
  );
}

function resetViewDate(
  mode
) {
  const today =
    new Date();

  if (
    mode === "month"
  ) {
    return startOfMonth(
      today
    );
  }

  return startOfDay(
    today
  );
}

function sortByExpectedDateThenStatus(
  rows
) {
  return [
    ...asArray(rows),
  ].sort(
    (a, b) => {
      const aDate =
        String(
          a.expected_delivery_date ||
            ""
        );

      const bDate =
        String(
          b.expected_delivery_date ||
            ""
        );

      const dateCmp =
        aDate.localeCompare(
          bDate
        );

      if (
        dateCmp !== 0
      ) {
        return dateCmp;
      }

      return String(
        a.po_number_display ||
          ""
      ).localeCompare(
        String(
          b.po_number_display ||
            ""
        )
      );
    }
  );
}

/* ───────────────── API payload normalization ───────────────── */

function normalizePOs(
  payload
) {
  if (
    Array.isArray(
      payload
    )
  ) {
    return payload;
  }

  if (
    Array.isArray(
      payload?.items
    )
  ) {
    return payload.items;
  }

  if (
    Array.isArray(
      payload?.rows
    )
  ) {
    return payload.rows;
  }

  if (
    Array.isArray(
      payload?.data
    )
  ) {
    return payload.data;
  }

  return [];
}

function normalizeStaff(
  payload
) {
  if (
    Array.isArray(
      payload
    )
  ) {
    return payload;
  }

  if (
    Array.isArray(
      payload?.staff
    )
  ) {
    return payload.staff;
  }

  if (
    Array.isArray(
      payload?.items
    )
  ) {
    return payload.items;
  }

  if (
    Array.isArray(
      payload?.rows
    )
  ) {
    return payload.rows;
  }

  if (
    Array.isArray(
      payload?.data
    )
  ) {
    return payload.data;
  }

  return [];
}

const fmtDate =
  (d) =>
    d
      ? format(
          d,
          "dd MMM yyyy"
        )
      : "";

const hasActiveDate =
  (range) =>
    Boolean(
      range?.[0]
        ?.startDate &&
        range?.[0]
          ?.endDate
    );

const getDateLabel =
  (range) => {
    const start =
      range?.[0]
        ?.startDate;

    const end =
      range?.[0]
        ?.endDate;

    if (
      !start ||
      !end
    ) {
      return "Any date";
    }

    return `${fmtDate(
      start
    )} ${ARROW} ${fmtDate(
      end
    )}`;
  };

const safeDate =
  (raw) => {
    const dateOnly =
      extractDateOnly(
        raw
      );

    const parsed =
      parseLocalDate(
        dateOnly
      );

    if (!parsed) {
      return raw
        ? String(raw)
        : "—";
    }

    return format(
      parsed,
      "yyyy-MM-dd"
    );
  };

function formatTimeRange(
  po
) {
  const raw =
    po?.expected_delivery_time ||
    po?.delivery_time ||
    po?.created_at ||
    null;

  if (!raw) {
    return "Date scheduled";
  }

  const d =
    new Date(raw);

  if (
    Number.isNaN(
      d.getTime()
    )
  ) {
    const s =
      String(raw);

    return s.includes(
      ":"
    )
      ? s.slice(0, 5)
      : "Date scheduled";
  }

  return format(
    d,
    "HH:mm"
  );
}

function getStaffDisplayName(
  staff
) {
  if (!staff) {
    return "";
  }

  const direct =
    staff.name ||
    staff.full_name ||
    staff.display_name;

  if (direct) {
    return String(
      direct
    ).trim();
  }

  const first =
    staff.first_name ||
    staff.firstname ||
    staff.firstName ||
    "";

  const last =
    staff.last_name ||
    staff.lastname ||
    staff.lastName ||
    "";

  const joined =
    `${first} ${last}`.trim();

  return (
    joined ||
    String(
      staff.email ||
        staff.username ||
        staff.staff_code ||
        ""
    ).trim()
  );
}

function getPoId(po) {
  return (
    po?.po_id ??
    po?.id ??
    po?.poId ??
    po?.purchase_order_id ??
    null
  );
}

function dedupePOs(
  rows
) {
  const map =
    new Map();

  for (
    const r of
      asArray(rows)
  ) {
    const id =
      getPoId(r);

    if (
      id == null
    ) {
      continue;
    }

    const prev =
      map.get(id);

    if (!prev) {
      map.set(
        id,
        r
      );

      continue;
    }

    const score =
      (obj) =>
        Object.values(
          obj || {}
        ).filter(
          (v) =>
            v !== null &&
            v !==
              undefined &&
            v !== ""
        ).length;

    if (
      score(r) >=
      score(prev)
    ) {
      map.set(
        id,
        r
      );
    }
  }

  return Array.from(
    map.values()
  );
}

const legacyPoNo =
  (poId) =>
    `PO-${String(
      Number(poId) ||
        0
    ).padStart(
      5,
      "0"
    )}`;

/* ───────────────── optional staff directory ───────────────── */

/*
 * Staff directory data is useful for the Receiver filter,
 * but it is NOT required to operate Goods Receiving.
 *
 * 401:
 *   Session/authentication failure → propagate.
 *
 * 403:
 *   Valid authenticated user without staffing authority.
 *   This is expected for Goods Receiving and must NOT break
 *   the receiving dashboard.
 *
 * 404 / endpoint mismatch:
 *   Continue trying compatibility candidates.
 */
async function fetchStaffDirectoryOptional() {
  const candidates = [
    "/staffing",
    "/staffing/",
    "/staff",
    "/staff/",
  ];

  for (
    const p of
      candidates
  ) {
    try {
      const res =
        await api.get(p);

      return {
        path: p,
        data:
          res?.data ??
          null,
        denied: false,
      };
    } catch (err) {
      const status =
        err?.response
          ?.status;

      if (
        status === 401
      ) {
        throw err;
      }

      if (
        status === 403
      ) {
        return {
          path: p,
          data: null,
          denied: true,
        };
      }

      /*
       * Other failures — including compatibility 404s —
       * move to the next candidate.
       */
    }
  }

  return {
    path: "",
    data: null,
    denied: false,
  };
}

/* ───────────────── PO retrieval ───────────────── */

const RECEIVABLE_STATUSES =
  new Set([
    "Approved",
    "Dispatched",
    "Receiving",
    "Partially Received",
    "Fully Received",
  ]);

async function fetchReceivablePOs() {
  const used = [];
  const collected = [];

  try {
    const res =
      await api.get(
        "/purchase-orders/dispatched"
      );

    const rows =
      normalizePOs(
        res?.data
      );

    if (
      rows.length
    ) {
      collected.push(
        ...rows
      );

      used.push(
        "/purchase-orders/dispatched"
      );
    }
  } catch (err) {
    const status =
      err?.response
        ?.status;

    if (
      status === 401 ||
      status === 403
    ) {
      throw err;
    }
  }

  try {
    const res =
      await api.get(
        "/purchase-orders/all"
      );

    const rows =
      normalizePOs(
        res?.data
      );

    if (
      rows.length
    ) {
      collected.push(
        ...rows
      );

      used.push(
        "/purchase-orders/all"
      );
    }
  } catch (err) {
    const status =
      err?.response
        ?.status;

    if (
      status === 401 ||
      status === 403
    ) {
      throw err;
    }
  }

  const deduped =
    dedupePOs(
      collected
    );

  const filtered =
    deduped.filter(
      (po) =>
        RECEIVABLE_STATUSES.has(
          String(
            po?.status ||
              ""
          ).trim()
        )
    );

  return {
    source:
      used.length
        ? used.join(
            " + "
          )
        : "none",

    rows:
      filtered,
  };
}

/* ───────────────── receiving state ───────────────── */

function getReceivingState(
  po
) {
  const status =
    String(
      po?.status ||
        ""
    ).trim();

  const dateKey =
    po?.expected_delivery_date ||
    "";

  const date =
    parseLocalDate(
      dateKey
    );

  const today =
    startOfDay(
      new Date()
    );

  if (
    status ===
    "Fully Received"
  ) {
    return {
      key: "complete",
      label:
        "Fully Received",
      tone: "good",
      percentTone:
        "text-emerald-700",
      dot:
        "bg-emerald-500",
      wrapper:
        "border-emerald-300 bg-emerald-50/90 hover:bg-emerald-50",
      badge:
        "text-emerald-700",
      borderAccent:
        "border-l-4 border-l-emerald-500",
    };
  }

  if (
    date &&
    date < today
  ) {
    return {
      key: "overdue",
      label:
        "Overdue",
      tone: "bad",
      percentTone:
        "text-rose-700",
      dot:
        "bg-rose-500",
      wrapper:
        "border-rose-300 bg-rose-50/90 hover:bg-rose-50",
      badge:
        "text-rose-700",
      borderAccent:
        "border-l-4 border-l-rose-500",
    };
  }

  if (
    status ===
      "Receiving" ||
    status ===
      "Partially Received"
  ) {
    return {
      key:
        "receiving",
      label:
        "Receiving",
      tone:
        "warn",
      percentTone:
        "text-amber-700",
      dot:
        "bg-amber-500",
      wrapper:
        "border-amber-300 bg-amber-50/90 hover:bg-amber-50",
      badge:
        "text-amber-700",
      borderAccent:
        "border-l-4 border-l-amber-500",
    };
  }

  if (
    status ===
    "Dispatched"
  ) {
    return {
      key:
        "scheduled",
      label:
        "Dispatched",
      tone:
        "info",
      percentTone:
        "text-sky-700",
      dot:
        "bg-sky-500",
      wrapper:
        "border-sky-300 bg-sky-50/90 hover:bg-sky-50",
      badge:
        "text-sky-700",
      borderAccent:
        "border-l-4 border-l-sky-500",
    };
  }

  if (
    status ===
    "Approved"
  ) {
    return {
      key:
        "approved",
      label:
        "Approved",
      tone:
        "neutral",
      percentTone:
        "text-slate-700",
      dot:
        "bg-slate-500",
      wrapper:
        "border-slate-300 bg-slate-50 hover:bg-slate-100",
      badge:
        "text-slate-700",
      borderAccent:
        "border-l-4 border-l-slate-400",
    };
  }

  return {
    key:
      "neutral",
    label:
      status ||
      "Pending",
    tone:
      "neutral",
    percentTone:
      "text-slate-700",
    dot:
      "bg-slate-400",
    wrapper:
      "border-slate-300 bg-white hover:bg-slate-50",
    badge:
      "text-slate-700",
    borderAccent:
      "border-l-4 border-l-slate-300",
  };
}

function getCompletionPercent(
  po
) {
  const fulfilled =
    Number(
      po?.items_fulfilled ||
        0
    );

  const total =
    Number(
      po?.items_total ||
        0
    );

  if (
    String(
      po?.status ||
        ""
    ).trim() ===
    "Fully Received"
  ) {
    return 100;
  }

  if (
    total <= 0
  ) {
    if (
      String(
        po?.status ||
          ""
      ).trim() ===
        "Receiving" ||
      String(
        po?.status ||
          ""
      ).trim() ===
        "Partially Received"
    ) {
      return 50;
    }

    if (
      String(
        po?.status ||
          ""
      ).trim() ===
      "Dispatched"
    ) {
      return 0;
    }

    return 0;
  }

  return Math.max(
    0,
    Math.min(
      100,
      Math.round(
        (fulfilled /
          total) *
          100
      )
    )
  );
}

function statusBadgeTone(
  po
) {
  const state =
    getReceivingState(
      po
    );

  if (
    state.key ===
    "complete"
  ) {
    return "good";
  }

  if (
    state.key ===
    "overdue"
  ) {
    return "bad";
  }

  if (
    state.key ===
    "receiving"
  ) {
    return "warn";
  }

  return "neutral";
}

function getFulfillmentText(
  po
) {
  const fulfilled =
    Number(
      po?.items_fulfilled ||
        0
    );

  const total =
    Number(
      po?.items_total ||
        0
    );

  if (
    total > 0
  ) {
    return `${fulfilled} of ${total} items received`;
  }

  return String(
    po?.status ||
      ""
  ).trim() ===
    "Fully Received"
    ? "Receipt completed"
    : "Awaiting item receipt detail";
}

function getReceiverName(
  po
) {
  return (
    po?.received_by_name ||
    po?.received_by ||
    po?.receivedByName ||
    po?.received_by_staff ||
    "—"
  );
}

/* ───────────────── small UI components ───────────────── */

function Pill({
  tone = "neutral",
  children,
  className = "",
}) {
  const toneClasses =
    tone === "good"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : tone ===
          "warn"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : tone ===
          "bad"
      ? "bg-rose-50 text-rose-800 border-rose-200"
      : tone ===
          "info"
      ? "bg-sky-50 text-sky-800 border-sky-200"
      : "bg-white text-slate-700 border-slate-200";

  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold",
        toneClasses,
        className
      )}
    >
      {children}
    </span>
  );
}

function StatTile({
  label,
  value,
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>

      <div className="mt-2 text-xl font-bold text-slate-900">
        {value}
      </div>
    </div>
  );
}

function ActionTopButton({
  onClick,
  disabled = false,
  icon: Icon,
  label,
}) {
  return (
    <button
      onClick={
        onClick
      }
      disabled={
        disabled
      }
      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
      type="button"
    >
      <Icon className="h-4 w-4" />

      {label}
    </button>
  );
}

function ReadinessBadge({
  label,
  tone = "neutral",
}) {
  return (
    <div
      className={classNames(
        "rounded-2xl border px-4 py-3",
        tone ===
          "good" &&
          "border-emerald-200 bg-emerald-50",
        tone ===
          "warn" &&
          "border-amber-200 bg-amber-50",
        tone ===
          "bad" &&
          "border-rose-200 bg-rose-50",
        tone ===
          "neutral" &&
          "border-slate-200 bg-slate-50"
      )}
    >
      <div className="flex items-center gap-2 text-sm font-semibold">
        {tone ===
        "good" ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-700" />
        ) : tone ===
          "bad" ? (
          <AlertTriangle className="h-4 w-4 text-rose-700" />
        ) : tone ===
          "warn" ? (
          <Clock3 className="h-4 w-4 text-amber-700" />
        ) : (
          <Truck className="h-4 w-4 text-slate-700" />
        )}

        <span
          className={classNames(
            tone ===
              "good" &&
              "text-emerald-800",
            tone ===
              "warn" &&
              "text-amber-800",
            tone ===
              "bad" &&
              "text-rose-800",
            tone ===
              "neutral" &&
              "text-slate-800"
          )}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

/* ───────────────── component ───────────────── */

export default function POReceivingDashboard() {
  const [
    purchaseOrders,
    setPurchaseOrders,
  ] =
    useState([]);

  const [
    poSource,
    setPoSource,
  ] =
    useState("");

  const [
    searchQuery,
    setSearchQuery,
  ] =
    useState("");

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    refreshing,
    setRefreshing,
  ] =
    useState(false);

  const [
    error,
    setError,
  ] =
    useState("");

  const [
    staffList,
    setStaffList,
  ] =
    useState([]);

  const [
    staffWarning,
    setStaffWarning,
  ] =
    useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] =
    useState("all");

  const [
    staffFilter,
    setStaffFilter,
  ] =
    useState("all");

  const [
    dateRange,
    setDateRange,
  ] =
    useState([
      {
        startDate: null,
        endDate: null,
        key: "selection",
      },
    ]);

  const [
    poNumberById,
    setPoNumberById,
  ] =
    useState({});

  const [
    viewMode,
    setViewMode,
  ] =
    useState(
      "month"
    );

  const [
    viewDate,
    setViewDate,
  ] =
    useState(
      () =>
        startOfMonth(
          new Date()
        )
    );

  const navigate =
    useNavigate();

  const printRef =
    useRef(null);

  const fetchTruePoNumber =
    async (
      poId
    ) => {
      try {
        if (!poId) {
          return "";
        }

        if (
          poNumberById[
            poId
          ]
        ) {
          return poNumberById[
            poId
          ];
        }

        const {
          data,
        } =
          await api.get(
            `/purchase-orders/${poId}/summary`
          );

        const n =
          data?.header
            ?.po_number ||
          data?.po_number ||
          data?.poNumber ||
          "";

        const cleaned =
          n
            ? String(n)
            : "";

        if (
          cleaned
        ) {
          setPoNumberById(
            (prev) => ({
              ...prev,
              [poId]:
                cleaned,
            })
          );

          return cleaned;
        }

        return "";
      } catch {
        return "";
      }
    };

  const warmPoNumbers =
    async (
      rows
    ) => {
      const ids =
        asArray(rows)
          .map(
            (r) =>
              getPoId(
                r
              )
          )
          .filter(
            (id) =>
              id != null
          );

      const uniq = [
        ...new Set(
          ids
        ),
      ];

      const batch =
        uniq.slice(
          0,
          80
        );

      await Promise.all(
        batch.map(
          async (
            id
          ) => {
            if (
              poNumberById[
                id
              ]
            ) {
              return;
            }

            await fetchTruePoNumber(
              id
            );
          }
        )
      );
    };

  const displayPoNo =
    (po) => {
      const id =
        getPoId(po);

      const cached =
        id != null
          ? poNumberById[
              id
            ]
          : "";

      return (
        cached ||
        po?.po_number ||
        legacyPoNo(id)
      );
    };

  const enrichPO =
    (po) => {
      const poNumberDisplay =
        displayPoNo(
          po
        );

      const expectedDeliveryDate =
        extractDateOnly(
          po?.expected_delivery
        );

      const state =
        getReceivingState({
          ...po,
          expected_delivery_date:
            expectedDeliveryDate,
        });

      const completionPercent =
        getCompletionPercent(
          po
        );

      return {
        ...po,

        po_number_display:
          poNumberDisplay,

        expected_delivery_date:
          expectedDeliveryDate,

        expected_delivery_label:
          safeDate(
            po?.expected_delivery
          ),

        receiver_name:
          getReceiverName(
            po
          ),

        completion_percent:
          completionPercent,

        state,

        fulfillment_text:
          getFulfillmentText(
            po
          ),
      };
    };

  const loadAll =
    async ({
      silent = false,
    } = {}) => {
      if (!silent) {
        setLoading(
          true
        );

        setError(
          ""
        );
      } else {
        setRefreshing(
          true
        );

        setError(
          ""
        );
      }

      try {
        const [
          {
            source,
            rows,
          },
          staffRes,
        ] =
          await Promise.all([
            fetchReceivablePOs(),
            fetchStaffDirectoryOptional(),
          ]);

        setPurchaseOrders(
          rows
        );

        setPoSource(
          source
        );

        await warmPoNumbers(
          rows
        );

        const staffRows =
          normalizeStaff(
            staffRes?.data
          );

        const cleaned =
          asArray(
            staffRows
          )
            .map(
              (s) => ({
                ...s,

                __displayName:
                  getStaffDisplayName(
                    s
                  ),
              })
            )
            .filter(
              (s) =>
                s.__displayName
            );

        setStaffList(
          cleaned
        );

        /*
         * Lack of staffing permission is expected for a
         * Goods Receiving-only user.
         *
         * Do not show it as a Receiving dashboard error.
         */
        if (
          staffRes?.denied
        ) {
          setStaffWarning(
            ""
          );
        } else if (
          !cleaned.length &&
          staffRes?.data ==
            null
        ) {
          setStaffWarning(
            "Staff list not available."
          );
        } else {
          setStaffWarning(
            ""
          );
        }
      } catch (err) {
        console.error(
          "Dashboard load failed:",
          err?.response
            ?.data ||
            err?.message
        );

        setPurchaseOrders(
          []
        );

        const status =
          err?.response
            ?.status;

        setError(
          status ===
              401 ||
            status ===
              403
            ? "You don't have permission to view purchase orders."
            : "Failed to load purchase orders."
        );
      } finally {
        setLoading(
          false
        );

        setRefreshing(
          false
        );
      }
    };

  useEffect(() => {
    let cancelled =
      false;

    (async () => {
      if (
        cancelled
      ) {
        return;
      }

      await loadAll({
        silent: false,
      });
    })();

    return () => {
      cancelled =
        true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enrichedPOs =
    useMemo(
      () =>
        asArray(
          purchaseOrders
        ).map(
          enrichPO
        ),
      [
        purchaseOrders,
        poNumberById,
      ]
    );

  const applyFilters =
    (po) => {
      if (!po) {
        return false;
      }

      const query =
        toLower(
          searchQuery
        ).trim();

      const items =
        Array.isArray(
          po.items
        )
          ? po.items
          : [];

      const poId =
        getPoId(po);

      const displayNo =
        toLower(
          po.po_number_display
        );

      const legacyNo =
        toLower(
          po?.po_number ||
            ""
        );

      const idStr =
        String(
          poId ??
            ""
        );

      const matchesSearch =
        !query ||
        displayNo.includes(
          query
        ) ||
        legacyNo.includes(
          query
        ) ||
        idStr.includes(
          query
        ) ||
        toLower(
          po.vendor_name
        ).includes(
          query
        ) ||
        items.some(
          (item) =>
            toLower(
              item?.item_code
            ).includes(
              query
            )
        );

      const matchesStatus =
        statusFilter ===
          "all" ||
        String(
          po.status ||
            ""
        ) ===
          statusFilter;

      const matchesStaff =
        staffFilter ===
          "all" ||
        String(
          po.receiver_name ||
            ""
        ) ===
          staffFilter;

      const start =
        dateRange?.[0]
          ?.startDate;

      const end =
        dateRange?.[0]
          ?.endDate;

      const matchesDate =
        start &&
        end
          ? (() => {
              const d =
                parseLocalDate(
                  po.expected_delivery_date
                );

              if (!d) {
                return false;
              }

              const startDay =
                startOfDay(
                  start
                );

              const endDay =
                startOfDay(
                  end
                );

              return (
                d >=
                  startDay &&
                d <=
                  endDay
              );
            })()
          : true;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesStaff &&
        matchesDate
      );
    };

  const filteredPOs =
    useMemo(
      () =>
        sortByExpectedDateThenStatus(
          enrichedPOs.filter(
            applyFilters
          )
        ),
      [
        enrichedPOs,
        searchQuery,
        statusFilter,
        staffFilter,
        dateRange,
      ]
    );

  const visibleRange =
    useMemo(
      () =>
        getPeriodRange(
          viewDate,
          viewMode
        ),
      [
        viewDate,
        viewMode,
      ]
    );

  const calendarPOs =
    useMemo(() => {
      const startKey =
        formatLocalDateKey(
          visibleRange.start
        );

      const endKey =
        formatLocalDateKey(
          visibleRange.end
        );

      return filteredPOs.filter(
        (po) => {
          const key =
            po.expected_delivery_date;

          return (
            key &&
            key >=
              startKey &&
            key <=
              endKey
          );
        }
      );
    }, [
      filteredPOs,
      visibleRange,
    ]);

  const unscheduledPOs =
    useMemo(
      () =>
        filteredPOs.filter(
          (po) =>
            !po.expected_delivery_date
        ),
      [
        filteredPOs,
      ]
    );

  const posByDate =
    useMemo(() => {
      const map =
        new Map();

      for (
        const po of
          calendarPOs
      ) {
        const key =
          po.expected_delivery_date;

        if (
          !map.has(
            key
          )
        ) {
          map.set(
            key,
            []
          );
        }

        map.get(
          key
        ).push(po);
      }

      for (
        const [
          ,
          list,
        ] of map
      ) {
        list.sort(
          (
            a,
            b
          ) => {
            const aPct =
              Number(
                a.completion_percent ||
                  0
              );

            const bPct =
              Number(
                b.completion_percent ||
                  0
              );

            if (
              aPct !==
              bPct
            ) {
              return (
                aPct -
                bPct
              );
            }

            return String(
              a.po_number_display ||
                ""
            ).localeCompare(
              String(
                b.po_number_display ||
                  ""
              )
            );
          }
        );
      }

      return map;
    }, [
      calendarPOs,
    ]);

  const calendarCells =
    useMemo(() => {
      if (
        viewMode ===
        "day"
      ) {
        return [
          startOfDay(
            viewDate
          ),
        ];
      }

      if (
        viewMode ===
        "week"
      ) {
        return buildWeekGrid(
          viewDate
        );
      }

      return buildMonthGrid(
        viewDate
      );
    }, [
      viewDate,
      viewMode,
    ]);

  const boardPOs =
    useMemo(
      () =>
        sortByExpectedDateThenStatus(
          calendarPOs
        ),
      [
        calendarPOs,
      ]
    );

  const kpis =
    useMemo(() => {
      const total =
        filteredPOs.length;

      const inRange =
        calendarPOs.length;

      const complete =
        filteredPOs.filter(
          (po) =>
            po.state
              .key ===
            "complete"
        ).length;

      const overdue =
        filteredPOs.filter(
          (po) =>
            po.state
              .key ===
            "overdue"
        ).length;

      const receiving =
        filteredPOs.filter(
          (po) =>
            po.state
              .key ===
            "receiving"
        ).length;

      const averageCompletion =
        total > 0
          ? Math.round(
              filteredPOs.reduce(
                (
                  sum,
                  po
                ) =>
                  sum +
                  Number(
                    po.completion_percent ||
                      0
                  ),
                0
              ) /
                total
            )
          : 0;

      return {
        total,
        inRange,
        complete,
        overdue,
        receiving,
        averageCompletion,
      };
    }, [
      filteredPOs,
      calendarPOs,
    ]);

  const exportRows =
    useMemo(
      () =>
        filteredPOs.map(
          (po) => ({
            "PO ID":
              getPoId(
                po
              ),

            "PO Number":
              po.po_number_display,

            Vendor:
              po.vendor_name ||
              "—",

            "Expected Delivery":
              po.expected_delivery_label,

            Status:
              po.status ||
              "—",

            Receiver:
              po.receiver_name,

            "Items Fulfilled":
              po.items_fulfilled ||
              0,

            "Items Total":
              po.items_total ||
              0,

            "Completion %":
              `${po.completion_percent || 0}%`,
          })
        ),
      [
        filteredPOs,
      ]
    );

  const exportToCSV =
    () => {
      const headers =
        Object.keys(
          exportRows[0] ||
            {
              "PO ID":
                "",

              "PO Number":
                "",

              Vendor:
                "",

              "Expected Delivery":
                "",

              Status:
                "",

              Receiver:
                "",

              "Items Fulfilled":
                "",

              "Items Total":
                "",

              "Completion %":
                "",
            }
        );

      const rows =
        exportRows.map(
          (r) =>
            headers.map(
              (h) =>
                r[h]
            )
        );

      const esc =
        (v) => {
          const s =
            v == null
              ? ""
              : String(
                  v
                );

          return /[",\n]/.test(
            s
          )
            ? `"${s.replace(
                /"/g,
                '""'
              )}"`
            : s;
        };

      const csvContent =
        [
          headers,
          ...rows,
        ]
          .map(
            (r) =>
              r
                .map(
                  esc
                )
                .join(
                  ","
                )
          )
          .join(
            "\n"
          );

      const blob =
        new Blob(
          [
            csvContent,
          ],
          {
            type:
              "text/csv;charset=utf-8;",
          }
        );

      saveAs(
        blob,
        "po_receiving_dashboard.csv"
      );
    };

  const exportToPDF =
    () => {
      if (
        !printRef.current
      ) {
        return;
      }

      html2pdf()
        .from(
          printRef.current
        )
        .set({
          margin: 0.4,

          filename:
            "po_receiving_dashboard.pdf",

          html2canvas: {
            scale: 2,
          },

          jsPDF: {
            unit: "in",
            format: "a3",
            orientation:
              "landscape",
          },
        })
        .save();
    };

  const exportToExcel =
    () => {
      const worksheet =
        XLSX.utils.json_to_sheet(
          exportRows
        );

      const workbook = {
        Sheets: {
          "PO Receiving Dashboard":
            worksheet,
        },

        SheetNames: [
          "PO Receiving Dashboard",
        ],
      };

      const excelBuffer =
        XLSX.write(
          workbook,
          {
            bookType:
              "xlsx",
            type:
              "array",
          }
        );

      const blob =
        new Blob(
          [
            excelBuffer,
          ],
          {
            type:
              "application/octet-stream",
          }
        );

      saveAs(
        blob,
        "po_receiving_dashboard.xlsx"
      );
    };

  const pickerRange =
    useMemo(() => {
      const start =
        dateRange?.[0]
          ?.startDate;

      const end =
        dateRange?.[0]
          ?.endDate;

      const today =
        new Date();

      return [
        {
          startDate:
            start ||
            today,

          endDate:
            end ||
            start ||
            today,

          key:
            "selection",
        },
      ];
    }, [
      dateRange,
    ]);

  const clearFilters =
    () => {
      setSearchQuery(
        ""
      );

      setStatusFilter(
        "all"
      );

      setStaffFilter(
        "all"
      );

      setDateRange([
        {
          startDate: null,
          endDate: null,
          key:
            "selection",
        },
      ]);
    };

  const statusOptions =
    useMemo(() => {
      const set =
        new Set(
          asArray(
            purchaseOrders
          )
            .map(
              (po) =>
                String(
                  po?.status ||
                    ""
                ).trim()
            )
            .filter(
              Boolean
            )
        );

      [
        "Approved",
        "Dispatched",
        "Receiving",
        "Partially Received",
        "Fully Received",
      ].forEach(
        (s) =>
          set.add(s)
      );

      return [
        "all",
        ...Array.from(
          set
        ),
      ];
    }, [
      purchaseOrders,
    ]);

  const periodTitle =
    formatRangeTitle(
      viewDate,
      viewMode
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50 px-6 py-8 text-slate-900 md:px-10">
      <div
        className="mx-auto max-w-7xl space-y-6"
        ref={
          printRef
        }
      >
        <section className="rounded-[28px] border border-slate-200 bg-white/95 p-6 shadow-sm md:p-8">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0 flex-1">
              <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-800">
                <Truck className="h-3.5 w-3.5" />

                PO Receiving Command Centre
              </div>

              <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-950 md:text-5xl">
                Receiving Dashboard
              </h1>

              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 md:text-base">
                Calendar-first receiving visibility using expected delivery date,
                with colour-coded operational states and quick receiving actions.
              </p>

              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <StatTile
                  label="Filtered POs"
                  value={
                    kpis.total
                  }
                />

                <StatTile
                  label={`${viewMode
                    .charAt(
                      0
                    )
                    .toUpperCase()}${viewMode.slice(
                    1
                  )} In View`}
                  value={
                    kpis.inRange
                  }
                />

                <StatTile
                  label="Overdue"
                  value={
                    kpis.overdue
                  }
                />

                <StatTile
                  label="Avg Completion"
                  value={`${kpis.averageCompletion}%`}
                />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {poSource ? (
                  <Badge
                    variant="outline"
                    className="border-slate-200 bg-white text-slate-700"
                  >
                    Source:{" "}
                    {poSource}
                  </Badge>
                ) : null}

                {staffWarning ? (
                  <Badge
                    variant="outline"
                    className="border-amber-200 bg-amber-50 text-amber-800"
                  >
                    {
                      staffWarning
                    }
                  </Badge>
                ) : null}
              </div>
            </div>

            <div className="w-full xl:max-w-md">
              <div className="rounded-[28px] border border-slate-200 bg-gradient-to-br from-slate-50 to-cyan-50 p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Receiving Readiness
                    </div>

                    <div className="mt-1 text-xs text-slate-500">
                      Quick operational signal across current receiving workload.
                    </div>
                  </div>

                  <div className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                    {
                      kpis.averageCompletion
                    }
                    %
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <ReadinessBadge
                    label={`${kpis.complete} completed`}
                    tone="good"
                  />

                  <ReadinessBadge
                    label={`${kpis.receiving} in progress`}
                    tone="warn"
                  />

                  <ReadinessBadge
                    label={`${kpis.overdue} overdue`}
                    tone={
                      kpis.overdue >
                      0
                        ? "bad"
                        : "neutral"
                    }
                  />

                  <ReadinessBadge
                    label={`${kpis.total - kpis.complete} open`}
                    tone="neutral"
                  />
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  <ActionTopButton
                    onClick={() =>
                      loadAll(
                        {
                          silent: true,
                        }
                      )
                    }
                    disabled={
                      refreshing
                    }
                    icon={
                      RefreshCw
                    }
                    label={
                      refreshing
                        ? "Refreshing..."
                        : "Refresh"
                    }
                  />

                  <ActionTopButton
                    onClick={
                      exportToCSV
                    }
                    icon={
                      Download
                    }
                    label="CSV"
                  />

                  <ActionTopButton
                    onClick={
                      exportToPDF
                    }
                    icon={
                      Printer
                    }
                    label="PDF"
                  />

                  <ActionTopButton
                    onClick={
                      exportToExcel
                    }
                    icon={
                      FileSpreadsheet
                    }
                    label="Excel"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-[28px] border border-slate-200 bg-white/95 p-5 shadow-sm">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full lg:max-w-xl">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                <Input
                  type="text"
                  className="w-full rounded-2xl border-slate-300 bg-white pl-10 pr-4 py-3 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                  placeholder="Search PO number, vendor, or item code"
                  value={
                    searchQuery
                  }
                  onChange={(
                    e
                  ) =>
                    setSearchQuery(
                      e.target
                        .value
                    )
                  }
                />
              </div>

              <div className="inline-flex overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 self-start">
                {[
                  "all",
                  "upcoming",
                  "past",
                ].map(
                  (
                    tabValue
                  ) => (
                    <button
                      key={
                        tabValue
                      }
                      onClick={() =>
                        setStatusFilter(
                          statusFilter
                        )
                      }
                      type="button"
                      className="hidden"
                    />
                  )
                )}

                <button
                  onClick={() =>
                    setStatusFilter(
                      statusFilter
                    )
                  }
                  type="button"
                  className="hidden"
                />

                <button
                  onClick={() => {}}
                  type="button"
                  className="hidden"
                />
              </div>

              <div className="inline-flex overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 self-start">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 bg-white px-4 py-3 text-sm font-semibold text-slate-900"
                >
                  <Calendar className="h-4 w-4" />

                  Expected delivery calendar
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1fr_220px_220px_280px_auto]">
              <div className="hidden xl:block" />

              <Select
                value={
                  statusFilter
                }
                onValueChange={
                  setStatusFilter
                }
              >
                <SelectTrigger className="rounded-2xl border-slate-300 bg-white text-slate-800">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>

                <SelectContent>
                  {statusOptions.map(
                    (s) => (
                      <SelectItem
                        key={
                          s
                        }
                        value={
                          s
                        }
                      >
                        {s ===
                        "all"
                          ? "All Status"
                          : s}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>

              <Select
                value={
                  staffFilter
                }
                onValueChange={
                  setStaffFilter
                }
              >
                <SelectTrigger className="rounded-2xl border-slate-300 bg-white text-slate-800">
                  <SelectValue placeholder="Receiver" />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="all">
                    All Receivers
                  </SelectItem>

                  {asArray(
                    staffList
                  ).map(
                    (
                      staff,
                      idx
                    ) => (
                      <SelectItem
                        key={
                          staff.staff_id ??
                          staff.id ??
                          `${staff.__displayName}-${idx}`
                        }
                        value={
                          staff.__displayName
                        }
                      >
                        {
                          staff.__displayName
                        }
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger
                  asChild
                >
                  <Button
                    variant="outline"
                    className="w-full justify-between rounded-2xl border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  >
                    <span className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-sky-600" />

                      {getDateLabel(
                        dateRange
                      )}
                    </span>

                    <span className="text-slate-400 text-xs">
                      Range
                    </span>
                  </Button>
                </PopoverTrigger>

                <PopoverContent className="w-auto p-0 bg-white border-slate-200">
                  <DateRange
                    editableDateInputs
                    onChange={(
                      item
                    ) => {
                      const sel =
                        item?.selection;

                      setDateRange(
                        [
                          {
                            startDate:
                              sel?.startDate ??
                              null,

                            endDate:
                              sel?.endDate ??
                              null,

                            key:
                              "selection",
                          },
                        ]
                      );
                    }}
                    moveRangeOnFirstSelection={
                      false
                    }
                    ranges={
                      pickerRange
                    }
                  />

                  <div className="px-3 pb-3 pt-2 text-[11px] text-slate-500">
                    This date filter uses expected delivery date.
                  </div>
                </PopoverContent>
              </Popover>

              <Button
                variant="outline"
                className="rounded-2xl border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                onClick={
                  clearFilters
                }
              >
                <X className="h-4 w-4 mr-2" />

                Clear
              </Button>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="mr-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Scope
                  </div>

                  {[
                    "day",
                    "week",
                    "month",
                  ].map(
                    (
                      mode
                    ) => (
                      <button
                        key={
                          mode
                        }
                        type="button"
                        onClick={() => {
                          setViewMode(
                            mode
                          );

                          setViewDate(
                            (
                              prev
                            ) => {
                              const base =
                                startOfDay(
                                  prev
                                );

                              if (
                                mode ===
                                "month"
                              ) {
                                return startOfMonth(
                                  base
                                );
                              }

                              return base;
                            }
                          );
                        }}
                        className={classNames(
                          "rounded-2xl border px-4 py-2.5 text-sm font-semibold transition",

                          viewMode ===
                            mode
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                        )}
                      >
                        {mode
                          .charAt(
                            0
                          )
                          .toUpperCase() +
                          mode.slice(
                            1
                          )}
                      </button>
                    )
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setViewDate(
                        (
                          d
                        ) =>
                          shiftViewDate(
                            d,
                            viewMode,
                            -1
                          )
                      )
                    }
                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100"
                    title="Previous"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  <div className="min-w-[200px] rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-center text-base font-bold text-slate-900">
                    {
                      periodTitle
                    }
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      setViewDate(
                        (
                          d
                        ) =>
                          shiftViewDate(
                            d,
                            viewMode,
                            1
                          )
                      )
                    }
                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100"
                    title="Next"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setViewDate(
                        resetViewDate(
                          viewMode
                        )
                      )
                    }
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                  >
                    Today
                  </button>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span>
                  Showing{" "}
                  <span className="font-semibold text-slate-900">
                    {
                      filteredPOs.length
                    }
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-slate-900">
                    {
                      purchaseOrders.length
                    }
                  </span>
                </span>

                {statusFilter !==
                  "all" && (
                  <Badge
                    variant="outline"
                    className="border-slate-200 bg-white text-slate-700"
                  >
                    Status:{" "}
                    {
                      statusFilter
                    }
                  </Badge>
                )}

                {staffFilter !==
                  "all" && (
                  <Badge
                    variant="outline"
                    className="border-slate-200 bg-white text-slate-700"
                  >
                    Receiver:{" "}
                    {
                      staffFilter
                    }
                  </Badge>
                )}

                {hasActiveDate(
                  dateRange
                ) && (
                  <Badge
                    variant="outline"
                    className="border-slate-200 bg-white text-slate-700"
                  >
                    {getDateLabel(
                      dateRange
                    )}
                  </Badge>
                )}

                {searchQuery.trim() && (
                  <Badge
                    variant="outline"
                    className="border-slate-200 bg-white text-slate-700"
                  >
                    Search:{" "}
                    {
                      searchQuery.trim()
                    }
                  </Badge>
                )}
              </div>
            </div>

            {loading ? (
              <div className="pt-2 text-slate-600">
                Loading purchase orders...
              </div>
            ) : error ? (
              <div className="pt-2 text-rose-700">
                {error}
              </div>
            ) : filteredPOs.length ===
              0 ? (
              <div className="pt-2 text-slate-600">
                No purchase orders match your current filters.
              </div>
            ) : null}
          </div>
        </section>

        {!loading &&
        !error ? (
          <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  Receiving Calendar
                </h2>

                <p className="mt-1 text-sm text-slate-600">
                  Scan purchase orders by expected delivery date, receiving state,
                  and progress.
                </p>
              </div>

              <div className="hidden md:flex items-center gap-2 text-xs">
                <Pill tone="good">
                  Fully received
                </Pill>

                <Pill tone="warn">
                  Receiving
                </Pill>

                <Pill tone="info">
                  Dispatched
                </Pill>

                <Pill tone="bad">
                  Overdue
                </Pill>
              </div>
            </div>

            {viewMode !==
            "day" ? (
              <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
                {[
                  "Mon",
                  "Tue",
                  "Wed",
                  "Thu",
                  "Fri",
                  "Sat",
                  "Sun",
                ].map(
                  (
                    label
                  ) => (
                    <div
                      key={
                        label
                      }
                      className="border-r border-slate-200 px-3 py-3 text-center text-sm font-semibold text-slate-600 last:border-r-0"
                    >
                      {
                        label
                      }
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
                {formatDayTitle(
                  viewDate
                )}
              </div>
            )}

            <div
              className={classNames(
                "grid",

                viewMode ===
                  "day"
                  ? "grid-cols-1"
                  : "grid-cols-1 md:grid-cols-7"
              )}
            >
              {calendarCells.map(
                (
                  cellDate
                ) => {
                  const inMonth =
                    viewMode !==
                      "month" ||
                    isSameMonth(
                      cellDate,
                      viewDate
                    );

                  const key =
                    formatLocalDateKey(
                      cellDate
                    );

                  const dayPOs =
                    posByDate.get(
                      key
                    ) ||
                    [];

                  return (
                    <div
                      key={
                        key
                      }
                      className={classNames(
                        "min-h-[180px] border-b border-r border-slate-200 p-2.5",

                        viewMode ===
                          "day" &&
                          "min-h-[340px] border-r-0",

                        !inMonth &&
                          "bg-slate-50/60",

                        inMonth &&
                          "bg-white"
                      )}
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <div
                          className={classNames(
                            "inline-flex items-center justify-center rounded-full text-sm font-bold",

                            viewMode ===
                              "day"
                              ? "bg-slate-100 px-3 py-1 text-slate-900"
                              : inMonth
                              ? "h-8 w-8 bg-slate-100 text-slate-900"
                              : "h-8 w-8 bg-transparent text-slate-400"
                          )}
                        >
                          {viewMode ===
                          "day"
                            ? new Intl.DateTimeFormat(
                                "en-GB",
                                {
                                  weekday:
                                    "short",
                                  day:
                                    "numeric",
                                  month:
                                    "short",
                                }
                              ).format(
                                cellDate
                              )
                            : cellDate.getDate()}
                        </div>

                        {dayPOs.length ? (
                          <span className="text-[11px] font-semibold text-slate-500">
                            {
                              dayPOs.length
                            }{" "}
                            PO
                            {dayPOs.length ===
                            1
                              ? ""
                              : "s"}
                          </span>
                        ) : null}
                      </div>

                      <div className="space-y-2">
                        {dayPOs
                          .slice(
                            0,
                            viewMode ===
                              "day"
                              ? 12
                              : 4
                          )
                          .map(
                            (
                              po
                            ) => {
                              const tone =
                                po.state;

                              return (
                                <button
                                  key={
                                    getPoId(
                                      po
                                    ) ??
                                    po.po_number_display
                                  }
                                  id={`po-card-${
                                    getPoId(
                                      po
                                    ) ??
                                    po.po_number_display
                                  }`}
                                  type="button"
                                  onClick={() => {
                                    const poId =
                                      getPoId(
                                        po
                                      );

                                    if (
                                      poId !=
                                      null
                                    ) {
                                      navigate(
                                        `/po-receiving/${poId}`
                                      );
                                    }
                                  }}
                                  className={classNames(
                                    "w-full rounded-2xl border p-3 text-left shadow-sm transition",
                                    tone.wrapper,
                                    tone.borderAccent
                                  )}
                                  title={`Open ${po.po_number_display}`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <div className="truncate text-sm font-bold text-slate-900">
                                        {
                                          po.po_number_display
                                        }
                                      </div>

                                      <div className="truncate text-xs text-slate-600">
                                        {po.vendor_name ||
                                          "Vendor TBC"}
                                      </div>
                                    </div>

                                    <div
                                      className={classNames(
                                        "shrink-0 text-[11px] font-semibold",
                                        tone.badge
                                      )}
                                    >
                                      {
                                        po.completion_percent
                                      }
                                      %
                                    </div>
                                  </div>

                                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-700">
                                    <Clock3 className="h-3.5 w-3.5" />

                                    <span>
                                      {viewMode ===
                                      "day"
                                        ? formatTimeRange(
                                            po
                                          )
                                        : po.expected_delivery_label}
                                    </span>
                                  </div>

                                  <div className="mt-2 flex items-center justify-between gap-2">
                                    <div className="inline-flex items-center gap-1.5 rounded-full border border-white/70 bg-white/70 px-2 py-1 text-[11px] font-semibold text-slate-700">
                                      <span
                                        className={classNames(
                                          "h-2 w-2 rounded-full",
                                          tone.dot
                                        )}
                                      />

                                      {
                                        tone.label
                                      }
                                    </div>

                                    <div className="text-[11px] font-semibold text-slate-700">
                                      {po.items_fulfilled ||
                                        0}
                                      /
                                      {po.items_total ||
                                        0}
                                    </div>
                                  </div>
                                </button>
                              );
                            }
                          )}

                        {viewMode !==
                          "day" &&
                        dayPOs.length >
                          4 ? (
                          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500">
                            +
                            {dayPOs.length -
                              4}{" "}
                            more PO
                            {dayPOs.length -
                              4 ===
                            1
                              ? ""
                              : "s"}
                          </div>
                        ) : null}

                        {dayPOs.length ===
                          0 &&
                        viewMode ===
                          "day" ? (
                          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                            No purchase orders scheduled for this day.
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </section>
        ) : null}

        {!loading &&
        !error &&
        boardPOs.length >
          0 ? (
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 md:flex-row md:items-end md:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {
                    periodTitle
                  }{" "}
                  Receiving Board
                </h2>

                <p className="mt-1 text-sm text-slate-600">
                  Detailed operational view for purchase orders in the selected{" "}
                  {viewMode}.
                </p>
              </div>

              <div className="text-xs text-slate-500">
                {
                  boardPOs.length
                }{" "}
                PO
                {boardPOs.length ===
                1
                  ? ""
                  : "s"}{" "}
                in view
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
              {boardPOs.map(
                (
                  po
                ) => {
                  const poId =
                    getPoId(
                      po
                    );

                  return (
                    <div
                      key={
                        poId ??
                        po.po_number_display
                      }
                      className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex flex-col gap-4 border-b border-slate-200 pb-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              onClick={() => {
                                if (
                                  poId !=
                                  null
                                ) {
                                  navigate(
                                    `/po-receiving/${poId}`
                                  );
                                }
                              }}
                              type="button"
                              className="text-left text-xl font-bold text-slate-900 transition hover:text-sky-700"
                              title="Open PO"
                            >
                              {
                                po.po_number_display
                              }
                            </button>

                            <Pill
                              tone={statusBadgeTone(
                                po
                              )}
                            >
                              {
                                po
                                  .state
                                  .label
                              }
                            </Pill>
                          </div>

                          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span className="inline-flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5" />

                              {
                                po.expected_delivery_label
                              }
                            </span>

                            <span>
                              •
                            </span>

                            <span className="inline-flex items-center gap-1">
                              <Truck className="h-3.5 w-3.5" />

                              Vendor:{" "}
                              {po.vendor_name ||
                                "—"}
                            </span>

                            <span>
                              •
                            </span>

                            <span className="inline-flex items-center gap-1">
                              <PackageCheck className="h-3.5 w-3.5" />

                              {
                                po.fulfillment_text
                              }
                            </span>

                            <span>
                              •
                            </span>

                            <span className="truncate">
                              Receiver:{" "}
                              {
                                po.receiver_name
                              }
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            onClick={() => {
                              if (
                                poId !=
                                null
                              ) {
                                navigate(
                                  `/po-receiving/${poId}`
                                );
                              }
                            }}
                            type="button"
                            className="inline-flex items-center gap-1 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-white"
                          >
                            <Eye className="h-3.5 w-3.5" />

                            Open
                          </button>

                          <button
                            onClick={() => {
                              if (
                                poId !=
                                null
                              ) {
                                navigate(
                                  `/po-receiving/${poId}`
                                );
                              }
                            }}
                            type="button"
                            className="inline-flex items-center gap-1 rounded-2xl bg-sky-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-sky-700"
                          >
                            <PackageCheck className="h-3.5 w-3.5" />

                            Receive
                          </button>
                        </div>
                      </div>

                      <div className="space-y-5 pt-5">
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                          <StatTile
                            label="Status"
                            value={
                              po.status ||
                              "—"
                            }
                          />

                          <StatTile
                            label="Completion"
                            value={`${po.completion_percent}%`}
                          />

                          <StatTile
                            label="Items"
                            value={`${po.items_fulfilled || 0}/${po.items_total || 0}`}
                          />

                          <StatTile
                            label="PO ID"
                            value={
                              poId ??
                              "—"
                            }
                          />
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                            <Boxes className="h-4 w-4 text-sky-700" />

                            Receiving Snapshot
                          </div>

                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                              <div className="text-xs uppercase tracking-wide text-slate-500">
                                Expected Delivery
                              </div>

                              <div className="mt-1 text-lg font-bold text-slate-900">
                                {
                                  po.expected_delivery_label
                                }
                              </div>

                              <div className="mt-2 text-xs text-slate-500">
                                Calendar anchor for receiving operations.
                              </div>
                            </div>

                            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                              <div className="text-xs uppercase tracking-wide text-slate-500">
                                Operational Signal
                              </div>

                              <div className="mt-1 text-lg font-bold text-slate-900">
                                {
                                  po
                                    .state
                                    .label
                                }
                              </div>

                              <div className="mt-2 text-xs text-slate-500">
                                {
                                  po.fulfillment_text
                                }
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                          <ActionTopButton
                            onClick={() => {
                              if (
                                poId !=
                                null
                              ) {
                                navigate(
                                  `/po-receiving/${poId}`
                                );
                              }
                            }}
                            icon={
                              PackageCheck
                            }
                            label="Receive"
                          />

                          <ActionTopButton
                            onClick={() => {
                              if (
                                poId !=
                                null
                              ) {
                                navigate(
                                  `/po-receiving/${poId}`
                                );
                              }
                            }}
                            icon={
                              Eye
                            }
                            label="View"
                          />

                          <ActionTopButton
                            onClick={
                              exportToPDF
                            }
                            icon={
                              Printer
                            }
                            label="PDF"
                          />

                          <ActionTopButton
                            onClick={
                              exportToExcel
                            }
                            icon={
                              FileSpreadsheet
                            }
                            label="Excel"
                          />
                        </div>
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </section>
        ) : null}

        {!loading &&
        !error &&
        unscheduledPOs.length >
          0 ? (
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  Unscheduled Purchase Orders
                </h2>

                <p className="mt-1 text-sm text-slate-600">
                  Purchase orders without an expected delivery date.
                </p>
              </div>

              <div className="text-xs text-slate-500">
                {
                  unscheduledPOs.length
                }{" "}
                unscheduled
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {unscheduledPOs.map(
                (
                  po
                ) => {
                  const poId =
                    getPoId(
                      po
                    );

                  return (
                    <Card
                      key={
                        poId ??
                        po.po_number_display
                      }
                      className="border-slate-200 bg-slate-50 shadow-sm transition hover:shadow-md"
                    >
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3 mb-2">
                          <Boxes
                            className="text-slate-500"
                            size={
                              20
                            }
                          />

                          <h2 className="text-lg font-semibold text-slate-900">
                            {
                              po.po_number_display
                            }
                          </h2>
                        </div>

                        <p className="text-sm text-slate-600">
                          Vendor:{" "}
                          {po.vendor_name ||
                            "—"}
                        </p>

                        <p className="text-sm text-slate-600">
                          Expected Delivery: Missing
                        </p>

                        <div className="mt-3">
                          <Pill tone="neutral">
                            Needs expected date
                          </Pill>
                        </div>

                        <div className="mt-4">
                          <Button
                            variant="outline"
                            className="w-full rounded-2xl border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                            onClick={() => {
                              if (
                                poId !=
                                null
                              ) {
                                navigate(
                                  `/po-receiving/${poId}`
                                );
                              }
                            }}
                          >
                            Open PO
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                }
              )}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}